import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { PageHero } from "@/components/common/PageHero";
import { BookingScheduler } from "@/components/booking/BookingScheduler";
import { BRAND, WHATSAPP_LINK, openWhatsApp } from "@/data/site";
import { useT } from "@/context/LanguageContext";
import { Envelope, WhatsappLogo, Phone, CheckCircle, Package, CalendarCheck, ChatText, ShieldCheck, Buildings } from "@phosphor-icons/react";

const TABS = [
  { id: "quote", tkey: "contact.tabQuote", icon: Package },
  { id: "consultation", tkey: "contact.tabConsult", icon: CalendarCheck },
  { id: "supplier", tkey: "contact.tabSupplier", icon: ShieldCheck },
  { id: "audit", tkey: "contact.tabAudit", icon: Buildings },
  { id: "general", tkey: "contact.tabGeneral", icon: ChatText },
];

const field = "w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";

function SuccessBox({ message }) {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center rounded-xl bg-teal/10 p-10 text-center" data-testid="contact-success">
      <CheckCircle size={56} weight="fill" className="text-teal" />
      <h3 className="mt-4 font-heading text-xl font-semibold text-ink">{t("contact.thankYou")}</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-600">{message}</p>
    </div>
  );
}

export default function Contact() {
  const { t: tr } = useT();
  const [params, setParams] = useSearchParams();
  const initial = TABS.some((t) => t.id === params.get("type")) ? params.get("type") : "quote";
  const [tab, setTab] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({});
  const [slotRefresh, setSlotRefresh] = useState(0);

  useEffect(() => { setTab(initial); /* eslint-disable-next-line */ }, [params]);
  useEffect(() => { setForm({}); setSuccess(""); }, [tab]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (tab === "consultation" && (!form.date || !form.time)) {
      toast.error(tr("booking.chooseSlotFirst"));
      return;
    }
    setLoading(true);
    try {
      let res;
      if (tab === "quote") res = await api.post("/quote-requests", { ...form, product_details: form.product_details || form.message });
      else if (tab === "consultation") res = await api.post("/bookings", form);
      else if (tab === "supplier") res = await api.post("/supplier-verification", form);
      else if (tab === "audit") res = await api.post("/factory-audit", form);
      else res = await api.post("/leads", { ...form, source: "contact" });
      const msgKey = tab === "quote" ? "contact.successQuote"
        : tab === "consultation" ? "booking.success"
        : tab === "supplier" ? "contact.successSupplier"
        : tab === "audit" ? "contact.successAudit"
        : "contact.successGeneral";
      setSuccess(tr(msgKey));
      toast.success(tr("contact.thankYou"));
      const eventName = tab === "quote" ? "quote_request"
        : tab === "consultation" ? "booking_confirmed"
        : tab === "supplier" ? "supplier_verification_request"
        : tab === "audit" ? "factory_audit_request"
        : "contact_lead";
      trackEvent(eventName, { form_type: tab });
    } catch (err) {
      if (tab === "consultation" && err.response?.status === 409) {
        toast.error(tr("booking.slotTaken"));
        setForm((f) => ({ ...f, time: "" }));
        setSlotRefresh((n) => n + 1);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (id) => { setTab(id); setParams({ type: id }); };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline={tr("contact.overline")}
        title={tr("contact.title")}
        subtitle={tr("contact.subtitle")}
        crumbs={[{ label: tr("contact.overline") }]}
        image="https://images.pexels.com/photos/15674629/pexels-photo-15674629.jpeg"
      />
      <section className="bg-mist py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
              <div className="mb-7 flex flex-wrap gap-2 border-b border-slate-100 pb-5">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => switchTab(t.id)}
                    data-testid={`contact-tab-${t.id}`}
                    className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                      tab === t.id ? "bg-ink text-white" : "bg-mist text-slate-600 hover:text-ink"
                    }`}
                  >
                    <t.icon size={18} weight="duotone" /> {tr(t.tkey)}
                  </button>
                ))}
              </div>

              {success ? (
                <SuccessBox message={success} />
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input required placeholder={tr("contact.name")} value={form.name || ""} onChange={set("name")} data-testid="contact-name" className={field} />
                    <input required type="email" placeholder={tr("contact.email")} value={form.email || ""} onChange={set("email")} data-testid="contact-email" className={field} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input placeholder={tr("contact.phone")} value={form.phone || ""} onChange={set("phone")} data-testid="contact-phone" className={field} />
                    <input placeholder={tr("contact.company")} value={form.company || ""} onChange={set("company")} data-testid="contact-company" className={field} />
                  </div>

                  {tab === "quote" && (
                    <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <input placeholder={tr("contact.quantity")} value={form.quantity || ""} onChange={set("quantity")} data-testid="contact-quantity" className={field} />
                        <input placeholder={tr("contact.destination")} required value={form.destination || ""} onChange={set("destination")} data-testid="contact-destination" className={field} />
                      </div>
                      <textarea required rows={4} placeholder={tr("contact.product")} value={form.product_details || ""} onChange={set("product_details")} data-testid="contact-product" className={field} />
                    </>
                  )}

                  {tab === "consultation" && (
                    <>
                      <BookingScheduler
                        date={form.date}
                        time={form.time}
                        onDate={(d) => setForm((f) => ({ ...f, date: d }))}
                        onTime={(tm) => setForm((f) => ({ ...f, time: tm }))}
                        refresh={slotRefresh}
                      />
                      <input placeholder={tr("contact.topic")} value={form.topic || ""} onChange={set("topic")} data-testid="contact-topic" className={field} />
                      <textarea rows={3} placeholder={tr("contact.consultMsg")} value={form.message || ""} onChange={set("message")} data-testid="contact-message" className={field} />
                    </>
                  )}

                  {tab === "general" && (
                    <textarea required rows={5} placeholder={tr("contact.generalMsg")} value={form.message || ""} onChange={set("message")} data-testid="contact-message" className={field} />
                  )}

                  {tab === "supplier" && (
                    <>
                      <input required placeholder={tr("contact.supplierName")} value={form.supplier_name || ""} onChange={set("supplier_name")} data-testid="contact-supplier-name" className={field} />
                      <input placeholder={tr("contact.supplierContact")} value={form.supplier_contact || ""} onChange={set("supplier_contact")} data-testid="contact-supplier-contact" className={field} />
                      <input placeholder={tr("contact.productInterest")} value={form.product || ""} onChange={set("product")} data-testid="contact-supplier-product" className={field} />
                      <textarea rows={3} placeholder={tr("contact.supplierNotes")} value={form.message || ""} onChange={set("message")} data-testid="contact-message" className={field} />
                    </>
                  )}

                  {tab === "audit" && (
                    <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <input required placeholder={tr("contact.factoryName")} value={form.factory_name || ""} onChange={set("factory_name")} data-testid="contact-factory-name" className={field} />
                        <input placeholder={tr("contact.factoryLocation")} value={form.factory_location || ""} onChange={set("factory_location")} data-testid="contact-factory-location" className={field} />
                      </div>
                      <select value={form.audit_type || ""} onChange={set("audit_type")} data-testid="contact-audit-type" className={field}>
                        <option value="">{tr("contact.auditType")}</option>
                        <option>On-site capability audit</option>
                        <option>Business license verification</option>
                        <option>Social & compliance audit</option>
                        <option>Pre-shipment inspection</option>
                      </select>
                      <input placeholder={tr("contact.productInterest")} value={form.product || ""} onChange={set("product")} data-testid="contact-audit-product" className={field} />
                      <textarea rows={3} placeholder={tr("contact.auditNotes")} value={form.message || ""} onChange={set("message")} data-testid="contact-message" className={field} />
                    </>
                  )}

                  <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                    {loading ? tr("contact.submitting") : tab === "quote" ? tr("contact.submitQuote") : tab === "consultation" ? tr("booking.bookBtn") : tab === "supplier" ? tr("contact.submitSupplier") : tab === "audit" ? tr("contact.submitAudit") : tr("contact.submitGeneral")}
                  </button>
                </form>
              )}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl bg-ink p-7 text-white">
              <h3 className="font-heading text-lg font-semibold">{tr("contact.talkTo")}</h3>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-center gap-3"><Envelope size={20} className="text-teal-light" /><a href={`mailto:${BRAND.email}`} className="hover:text-teal-light">{BRAND.email}</a></li>
                <li className="flex items-center gap-3"><Phone size={20} className="text-teal-light" /><span>{BRAND.phone}</span></li>
                <li className="flex items-center gap-3"><WhatsappLogo size={20} className="text-teal-light" /><a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noopener noreferrer" className="hover:text-teal-light">{tr("contact.chatWhatsapp")}</a></li>
              </ul>
              <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="mt-6 flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold">
                <WhatsappLogo size={18} weight="fill" /> {tr("contact.messageUs")}
              </a>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-7">
              <h3 className="font-heading text-base font-semibold text-ink">{tr("contact.whyChoose")}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                {[tr("contact.why1"), tr("contact.why2"), tr("contact.why3"), tr("contact.why4")].map((x) => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-teal" /> {x}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
